import { useEffect, useMemo, useState } from 'react'
import { BellRing, Download, Info, X } from 'lucide-react'
import { C } from '../lib/tokens'
import {
  canUsePushApis,
  getExistingPushSubscription,
  getPortalPushConfig,
  isIos,
  isStandalonePwa,
  pushPermission,
  subscribeToPortalPush,
} from '../lib/pwa'

const DISMISS_KEY = 'kis_rx_push_prompt_dismissed_at_v1'
const DISMISS_DAYS = 14

function recentlyDismissed() {
  try {
    const value = Number(window.localStorage.getItem(DISMISS_KEY) || 0)
    if (!value) return false
    return Date.now() - value < DISMISS_DAYS * 24 * 60 * 60 * 1000
  } catch {
    return false
  }
}

function rememberDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    // Ignore private browsing storage failures.
  }
}

export default function PushNotificationPrompt({ patient }) {
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [config, setConfig] = useState({ enabled: false, publicKey: '' })
  const [subscriptionActive, setSubscriptionActive] = useState(false)
  const [installPrompt, setInstallPrompt] = useState(null)
  const iosNeedsInstall = isIos() && !isStandalonePwa()
  const canPush = canUsePushApis()

  useEffect(() => {
    const onBeforeInstallPrompt = event => {
      event.preventDefault()
      setInstallPrompt(event)
      if (!recentlyDismissed()) setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  }, [])

  useEffect(() => {
    let mounted = true
    async function load() {
      if (!patient?.mso || recentlyDismissed()) return
      try {
        const nextConfig = await getPortalPushConfig()
        if (!mounted) return
        setConfig(nextConfig || { enabled: false, publicKey: '' })
        if (!nextConfig?.enabled && !installPrompt) return

        const existing = canPush ? await getExistingPushSubscription({ allowDev: true }) : null
        if (!mounted) return
        setSubscriptionActive(Boolean(existing))
        const permission = pushPermission()
        if (permission === 'granted' && existing) return
        if (permission === 'denied' && !installPrompt) return
        setVisible(true)
      } catch {
        if (mounted && installPrompt) setVisible(true)
      }
    }
    load()
    return () => { mounted = false }
  }, [patient?.mso, installPrompt, canPush])

  const copy = useMemo(() => {
    if (iosNeedsInstall) {
      return {
        title: 'Add KIS Rx to your Home Screen',
        body: 'iPhone notifications work from the installed web app. Open this portal in Safari, tap Share, then Add to Home Screen.',
        icon: Download,
        primary: 'Got it',
      }
    }
    if (installPrompt && !isStandalonePwa()) {
      return {
        title: 'Install KIS Rx',
        body: 'Use the portal like an app, then turn on secure updates for messages, labs, orders, and visits.',
        icon: Download,
        primary: 'Install app',
      }
    }
    return {
      title: 'Stay updated securely',
      body: 'Get privacy-safe alerts for new messages, lab updates, order changes, and visit reminders.',
      icon: BellRing,
      primary: 'Enable notifications',
    }
  }, [installPrompt, iosNeedsInstall])

  if (!visible || subscriptionActive) return null

  const Icon = copy.icon

  const dismiss = () => {
    rememberDismissed()
    setVisible(false)
  }

  const installApp = async () => {
    if (!installPrompt) return
    setBusy(true)
    try {
      await installPrompt.prompt()
      await installPrompt.userChoice.catch(() => null)
      setInstallPrompt(null)
      setMessage('KIS Rx is ready to install from your browser.')
    } finally {
      setBusy(false)
    }
  }

  const enableNotifications = async () => {
    if (iosNeedsInstall) {
      setMessage('After adding KIS Rx to your Home Screen, reopen it there and turn notifications on.')
      return
    }
    if (installPrompt && !isStandalonePwa()) {
      await installApp()
      return
    }
    setBusy(true)
    setMessage('')
    try {
      const result = await subscribeToPortalPush({ publicKey: config.publicKey, allowDev: true })
      if (result.ok) {
        setSubscriptionActive(true)
        setMessage('Notifications are on.')
        window.setTimeout(() => setVisible(false), 1200)
      } else {
        setMessage(result.reason === 'permission_not_granted'
          ? 'Notifications were not enabled. You can turn them on from browser settings.'
          : 'Notifications are not available in this browser yet.')
      }
    } catch (err) {
      setMessage(err.message || 'Could not turn notifications on.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <aside
      className="kis-pwa-prompt"
      aria-label="KIS Rx app notifications"
      style={{
        position: 'fixed',
        right: 24,
        bottom: 92,
        zIndex: 74,
        width: 'min(420px, calc(100vw - 32px))',
        borderRadius: 22,
        border: `1px solid ${C.border}`,
        background: 'rgba(255,255,255,0.94)',
        boxShadow: C.shadowStrong,
        backdropFilter: 'blur(24px) saturate(1.16)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 36px', gap: 12, alignItems: 'start' }}>
        <span style={{
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'linear-gradient(180deg, #EAFBF2 0%, #DCF8EA 100%)',
          color: C.greenDark,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon size={19} strokeWidth={2.25} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 820, color: C.text }}>{copy.title}</div>
          <div style={{ marginTop: 5, fontSize: 13, lineHeight: 1.42, color: C.textSecondary }}>{copy.body}</div>
          {message && (
            <div style={{ marginTop: 9, display: 'flex', gap: 6, alignItems: 'flex-start', color: C.blue, fontSize: 12.5, lineHeight: 1.35 }}>
              <Info size={14} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>{message}</span>
            </div>
          )}
          <div className="kis-pwa-prompt-actions" style={{ marginTop: 13, display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={enableNotifications}
              disabled={busy || (!config.enabled && !installPrompt && !iosNeedsInstall)}
              style={{
                minHeight: 40,
                border: 'none',
                borderRadius: 20,
                background: C.blue,
                color: '#FFFFFF',
                cursor: busy ? 'default' : 'pointer',
                padding: '0 16px',
                fontSize: 13.5,
                fontWeight: 780,
                boxShadow: C.blueShadow,
              }}
            >
              {busy ? 'Working...' : copy.primary}
            </button>
            <button
              type="button"
              onClick={dismiss}
              style={{
                minHeight: 40,
                border: `1px solid ${C.borderStrong}`,
                borderRadius: 20,
                background: '#FFFFFF',
                color: C.textSecondary,
                cursor: 'pointer',
                padding: '0 14px',
                fontSize: 13.5,
                fontWeight: 740,
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss notification prompt"
          onClick={dismiss}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            border: `1px solid ${C.border}`,
            background: 'rgba(248,250,253,0.9)',
            color: C.textTertiary,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} strokeWidth={2.2} />
        </button>
      </div>
    </aside>
  )
}
